/**
 * SSE log-stream transport (T12.3 / B7 spec).
 *
 * attachLiveLogStream probes the endpoint with fetch BEFORE constructing an
 * EventSource. Rationale (fixed live 2026-09-19): with no backend yet, the
 * host's SPA fallback serves index.html with 200 + text/html for /api/* —
 * EventSource aborts with a MIME error and the console log showed
 * 'EventSource's response has a MIME type ("text/html") that is not
 * "text/event-stream"'. Probing first means we never attach a doomed
 * EventSource: non-2xx, wrong MIME, or fetch failure → onError immediately
 * and the hook falls back to its demo stream without touching EventSource.
 */
export function attachLiveLogStream(url, { onMessage, onError } = {}) {
  let source = null;
  let cancelled = false;

  const fail = () => {
    if (cancelled) return;
    onError?.(new Error('live log stream unavailable'));
  };

  fetch(url, { headers: { Accept: 'text/event-stream' }, redirect: 'error' })
    .then((response) => {
      if (!response.ok) {
        fail();
        return;
      }
      const mime = (response.headers.get('content-type') || '').toLowerCase();
      if (!mime.includes('text/event-stream')) {
        // SPA fallback answered index.html — not a real stream endpoint.
        fail();
        return;
      }
      if (cancelled) return;
      source = new EventSource(url);
      source.onmessage = (event) => {
        try {
          onMessage?.(JSON.parse(event.data));
        } catch {
          onMessage?.({ message: event.data });
        }
      };
      source.onerror = () => {
        source?.close();
        source = null;
        fail();
      };
    })
    .catch(fail);

  return {
    close() {
      cancelled = true;
      source?.close();
      source = null;
    }
  };
}

/** Back-compat alias — same shape the hooks previously imported. */
export function createLogStream(url, options = {}) {
  return attachLiveLogStream(url, options);
}
